import { useDispatch, useSelector } from 'react-redux';
import './css/app.css'
import './css/common.css'
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { change_Current_Location, changeHeaderTitle } from '../store';
import axios from 'axios';
import { LoadingModal, MessageModal } from './component/modal/modal';
import { DownloadBtn } from './component/btn/btn';
import { useNavigate } from 'react-router-dom';
import { changeNum } from './common/function';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { SyncLoader } from "react-spinners";
import { FileX, Search, ChevronUp, ChevronDown, ChevronsUpDown, X } from 'lucide-react';

dayjs.extend(utc);
dayjs.extend(timezone);

const safeNum = (v) => changeNum(Number(v) || 0);

// ─── 컬럼 정의 ───
const COLUMNS = (isMerchant, isSuper) => {
    const cols = [
        { key: "id", text: isMerchant ? "MID" : "영업자ID", width: "80px", flex: 1, bodyAlign: "center", filterable: true, filterParam: isMerchant ? "mid" : "agentId" },
        { key: "name", text: isMerchant ? "가맹점명" : "영업자명", width: "120px", flex: 1.5, bodyAlign: "left", filterable: true, filterParam: "name" },
        { key: "available_balance", text: "가용잔액", width: "90px", flex: 1, bodyAlign: "right", sortable: true },
        { key: "total_balance", text: "총잔액", width: "90px", flex: 1, bodyAlign: "right", sortable: true },
        { key: "frozen_balance", text: "동결잔액", width: "90px", flex: 1, bodyAlign: "right", sortable: true },
        { key: "recent_deposit_sum", text: "최근입금", width: "90px", flex: 1, bodyAlign: "right", sortable: true },
        { key: "recent_withdraw_sum", text: "최근출금", width: "90px", flex: 1, bodyAlign: "right", sortable: true },
        { key: "last_transaction_at", text: "최종거래", width: "130px", bodyAlign: "center", sortable: true },
    ];
    if (isSuper) cols.push({ key: "action", text: "조정", width: "70px", bodyAlign: "center" });
    return cols;
};

// ─── UUID v4 (secure context 폴백 포함) ───
const generateUuid = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

const LARGE_AMOUNT_THRESHOLD = 100_000_000; // 1억원

// ─── 메인 컴포넌트 ───
function WalletBalance() {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('merchant');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        dispatch(change_Current_Location("지갑잔액현황"));
        dispatch(changeHeaderTitle({ mainTitle: "관리자", subTitle: "지갑 잔액 현황" }));
        setTimeout(() => setLoading(false), 500);
    }, []);

    return (
        <div className="content" id="contents">
            {loading ? <LoadingModal /> : ""}
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <BalanceTab type={activeTab} activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>
        </div>
    );
}

// ─── 잔액 탭 ───
function BalanceTab({ type, activeTab, setActiveTab }) {
    const navigate = useNavigate();
    const MyData = useSelector((state) => state.MyData);
    const isSuper = MyData?.role === "SUPER";

    const isMerchant = type === 'merchant';
    const apiPath = isMerchant ? "/api/wallet/balance/merchants" : "/api/wallet/balance/agents";
    const idField = isMerchant ? "mid" : "agent_id";
    const nameField = isMerchant ? "merchant_name" : "agent_name";
    const columns = COLUMNS(isMerchant, isSuper);

    const [page, setPage] = useState(1);
    const [maxFirstPage] = useState(30);
    const [listData, setListData] = useState({});
    const [listTrxData, setListTrxData] = useState([]);
    const [reload, setReload] = useState(false);
    const [spinner, setSpinner] = useState(false);

    // 조정 모달
    const [adjustTarget, setAdjustTarget] = useState(null);
    const [message, setMessage] = useState({ msg: "" });

    // 조정 성공 시 행/요약 인라인 갱신
    const handleAdjustSuccess = (resData) => {
        const targetIdValue = adjustTarget?.[idField];
        const delta = Number(resData?.amount) || 0;
        // adjusted_at는 KST 문자열. 기존 last_transaction_at는 UTC 기준으로 렌더되므로 UTC ISO로 변환해 저장.
        const adjustedAtUtc = resData?.adjusted_at
            ? dayjs.tz(resData.adjusted_at, 'YYYY-MM-DD HH:mm:ss', 'Asia/Seoul').utc().format()
            : undefined;
        setListTrxData((prev) => prev.map((r) => r[idField] === targetIdValue ? {
            ...r,
            total_balance: resData.balance_after,
            available_balance: resData.available_balance,
            frozen_balance: resData.frozen_balance,
            ...(adjustedAtUtc ? { last_transaction_at: adjustedAtUtc } : {}),
        } : r));
        setListData((prev) => prev?.summary ? {
            ...prev,
            summary: {
                ...prev.summary,
                total_total_balance: Number(prev.summary.total_total_balance || 0) + delta,
                total_available_balance: Number(prev.summary.total_available_balance || 0) + delta,
            },
        } : prev);
        setAdjustTarget(null);
        setMessage({ msg: `지갑 잔액 조정 완료 (TX#${resData?.tx_id ?? "-"})` });
    };

    // 정렬
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState(null);

    const handleSort = (key) => {
        if (sortKey === key) {
            if (sortDir === "asc") { setSortDir("desc"); }
            else { setSortKey(null); setSortDir(null); }
        } else {
            setSortKey(key); setSortDir("asc");
        }
        setPage(1);
        setReload(e => !e);
    };

    // 컬럼별 필터
    const [filters, setFilters] = useState({});
    const debounceRef = useRef(null);

    const handleFilterChange = (filterParam, value) => {
        const next = { ...filters, [filterParam]: value };
        setFilters(next);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            setReload(e => !e);
        }, 400);
    };

    const handleFilterKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (debounceRef.current) clearTimeout(debounceRef.current);
            setPage(1);
            setReload(e => !e);
        }
    };

    // 탭 전환 시 데이터 리셋
    const prevType = useRef(type);
    useEffect(() => {
        if (prevType.current !== type) {
            prevType.current = type;
            setFilters({});
            setSortKey(null);
            setSortDir(null);
            setListData({});
            setListTrxData([]);
            setPage(1);
            setReload(e => !e);
        }
    }, [type]);

    // 목록 조회
    useEffect(() => { ListData(); }, [page, maxFirstPage, reload]);

    const ListData = async () => {
        if (page === 1) setSpinner(true);
        try {
            const params = { page, limit: maxFirstPage };
            for (const col of columns) {
                if (col.filterable && col.filterParam && filters[col.filterParam]) {
                    params[col.filterParam] = filters[col.filterParam].trim().slice(0, 100);
                }
            }
            if (sortKey && sortDir) {
                params.sortBy = sortKey;
                params.sortDir = sortDir;
            }
            const response = await axios.get(process.env.REACT_APP_API + apiPath, {
                withCredentials: true, params,
            });
            let tmp = response?.data;
            if (tmp?.code < 2000) {
                setListData(tmp?.data ?? {});
                const list = tmp?.data?.list ?? [];
                if (page === 1) setListTrxData(list);
                else setListTrxData((prev) => [...prev, ...list]);
            } else if (tmp?.code === 9999) {
                navigate('/');
            }
        } catch (error) { console.error(error); }
        setSpinner(false);
    };

    // 무한스크롤
    const observerRef = useRef(null);
    const bodyRef = useRef(null);
    const headerRef = useRef(null);

    useEffect(() => {
        if (listTrxData.length === 0) return;
        const total = listData?.totCnt ?? 0;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && total >= page * maxFirstPage) setPage((prev) => prev + 1);
        });
        if (observerRef.current) observer.observe(observerRef.current);
        return () => { if (observerRef.current) observer.unobserve(observerRef.current); };
    }, [listTrxData, listData]);

    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        const handleScroll = () => { if (headerRef.current) headerRef.current.scrollLeft = el.scrollLeft; };
        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => { if (page === 1 && bodyRef.current) bodyRef.current.scrollTop = 0; }, [page]);

    // 엑셀
    const excelBtn = async () => {
        try {
            const params = {};
            for (const col of columns) {
                if (col.filterable && col.filterParam && filters[col.filterParam]) {
                    params[col.filterParam] = filters[col.filterParam].trim().slice(0, 100);
                }
            }
            const response = await axios.get(process.env.REACT_APP_API + apiPath + "/excel", {
                withCredentials: true, params,
            });
            const tmp = response?.data;
            if (tmp?.code < 2000) buildExcel(tmp?.data?.list ?? []);
        } catch (error) { console.error(error); }
    };

    const buildExcel = (exData) => {
        const arr = exData.map((t) => ({
            [isMerchant ? "MID" : "영업자ID"]: t[idField] ?? "",
            [isMerchant ? "가맹점명" : "영업자명"]: t[nameField] ?? "",
            "가용잔액": Number(t.available_balance) || 0,
            "총잔액": Number(t.total_balance) || 0,
            "동결잔액": Number(t.frozen_balance) || 0,
            "최근입금": Number(t.recent_deposit_sum) || 0,
            "최근출금": Number(t.recent_withdraw_sum) || 0,
            "최종거래": t.last_transaction_at ? dayjs.utc(t.last_transaction_at).tz('Asia/Seoul').format('YYYY-MM-DD HH:mm') : "",
        }));
        Excel(arr, isMerchant ? "지갑잔액_가맹점" : "지갑잔액_영업자");
    };

    // 셀 렌더링
    const renderCell = (col, data) => {
        const val = col.key === "id" ? data[idField] : col.key === "name" ? data[nameField] : data[col.key];
        switch (col.key) {
            case "id": return val ?? "-";
            case "name": return val ?? "-";
            case "available_balance":
                return <span style={{ color: "#059669", fontWeight: "600" }}>{safeNum(val)}</span>;
            case "total_balance":
                return safeNum(val);
            case "frozen_balance":
                return Number(val) > 0
                    ? <span style={{ color: "#dc2626", fontWeight: "600" }}>{safeNum(val)}</span>
                    : safeNum(val);
            case "recent_deposit_sum":
                return <span style={{ color: "#059669" }}>{safeNum(val)}</span>;
            case "recent_withdraw_sum":
                return <span style={{ color: "#dc2626" }}>{safeNum(val)}</span>;
            case "last_transaction_at":
                return val ? dayjs.utc(val).tz('Asia/Seoul').format('YYYY-MM-DD HH:mm') : "-";
            case "action":
                return (
                    <button
                        onClick={(e) => { e.stopPropagation(); setAdjustTarget(data); }}
                        style={{
                            padding: "4px 12px", fontSize: "12px", fontWeight: "600",
                            backgroundColor: "#1e3a5f", color: "#fff",
                            border: "none", borderRadius: "4px", cursor: "pointer",
                        }}
                    >조정</button>
                );
            default: return val ?? "-";
        }
    };

    const summary = listData?.summary;
    const totalCount = listData?.totCnt ?? 0;

    // 컬럼 flex 스타일
    const colFlex = (col) => {
        if (col.flex) return { flex: col.flex, minWidth: col.width, flexShrink: 1 };
        return { minWidth: col.width, width: col.width, flexShrink: 0 };
    };

    return (
        <div className="intro-y box" style={{ padding: "0", overflow: "hidden" }}>
            <MessageModal message={message} setMessage={setMessage} />
            {adjustTarget && (
                <AdjustModal
                    target={adjustTarget}
                    isMerchant={isMerchant}
                    onClose={() => setAdjustTarget(null)}
                    onSuccess={handleAdjustSuccess}
                    setMessage={setMessage}
                />
            )}
            {/* 상단 바: 탭 + 요약 + 엑셀 */}
            <div style={{ padding: "16px 20px 0", borderBottom: "1px solid #e5e7eb" }}>
                {/* 탭 */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div style={{ display: "flex", gap: "0" }}>
                        {["merchant", "agent"].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    padding: "10px 20px",
                                    fontSize: "13px",
                                    fontWeight: activeTab === tab ? "700" : "400",
                                    color: activeTab === tab ? "#1e3a5f" : "#9ca3af",
                                    background: "none",
                                    border: "none",
                                    borderBottom: activeTab === tab ? "2px solid #1e3a5f" : "2px solid transparent",
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                }}
                            >
                                {tab === "merchant" ? "가맹점" : "영업자"}
                            </button>
                        ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingBottom: "8px" }}>
                        <span style={{ fontSize: "12px", color: "#6b7280" }}>
                            총 <b style={{ color: "#1e3a5f" }}>{totalCount.toLocaleString()}</b>건
                        </span>
                        <DownloadBtn text={"엑셀"} onClick={excelBtn} />
                    </div>
                </div>
            </div>

            {/* 테이블 */}
            <div style={{ padding: "12px 20px 20px" }}>
                {/* 요약 한 줄 */}
                {summary && (
                    <div style={{ display: "flex", gap: "16px", padding: "0 0 8px", fontSize: "12px", color: "#6b7280" }}>
                        <span>가용잔액 <b style={{ color: "#059669" }}>{safeNum(summary.total_available_balance)}원</b></span>
                        <span>총잔액 <b style={{ color: "#2563eb" }}>{safeNum(summary.total_total_balance)}원</b></span>
                        <span>동결잔액 <b style={{ color: "#dc2626" }}>{safeNum(summary.total_frozen_balance)}원</b></span>
                    </div>
                )}

                <div style={{ border: "1px solid #e2e5e9", borderRadius: "6px", overflow: "hidden" }}>
                    {/* 헤더 */}
                    <div style={{ overflowX: "hidden" }} ref={headerRef}>
                        <div style={{
                            display: "flex",
                            backgroundColor: "#1e3a5f",
                            color: "#fff",
                            fontSize: "13px",
                            fontWeight: "500",
                        }}>
                            {columns.map((col, i) => (
                                <div
                                    key={i}
                                    style={{
                                        ...colFlex(col),
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "0",
                                        borderRight: i < columns.length - 1 ? "1px solid rgba(255,255,255,0.15)" : "none",
                                        position: "relative",
                                    }}
                                >
                                    {col.filterable ? (
                                        <div style={{ position: "relative", width: "100%", display: "flex", alignItems: "center" }}>
                                            <Search size={13} style={{ position: "absolute", left: "8px", opacity: 0.5, pointerEvents: "none" }} />
                                            <input
                                                className="wallet-header-filter"
                                                type="text"
                                                value={filters[col.filterParam] || ""}
                                                onChange={(e) => handleFilterChange(col.filterParam, e.target.value)}
                                                onKeyDown={handleFilterKeyDown}
                                                placeholder={col.text}
                                                style={{
                                                    width: "100%",
                                                    height: "38px",
                                                    padding: "0 8px 0 26px",
                                                    fontSize: "13px",
                                                    border: "none",
                                                    outline: "none",
                                                    backgroundColor: "transparent",
                                                    color: "#fff",
                                                    boxSizing: "border-box",
                                                }}
                                            />
                                        </div>
                                    ) : col.sortable ? (
                                        <div
                                            onClick={() => handleSort(col.key)}
                                            style={{
                                                display: "flex", alignItems: "center", justifyContent: "center", gap: "3px",
                                                width: "100%", height: "38px",
                                                cursor: "pointer", userSelect: "none",
                                                padding: "0 6px",
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)"}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                        >
                                            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{col.text}</span>
                                            {sortKey === col.key ? (
                                                sortDir === "asc"
                                                    ? <ChevronUp size={14} style={{ flexShrink: 0 }} />
                                                    : <ChevronDown size={14} style={{ flexShrink: 0 }} />
                                            ) : (
                                                <ChevronsUpDown size={14} style={{ flexShrink: 0, opacity: 0.4 }} />
                                            )}
                                        </div>
                                    ) : (
                                        <span style={{ padding: "0 8px", lineHeight: "38px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {col.text}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 바디 */}
                    <div style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)", minHeight: "300px" }} ref={bodyRef}>
                        {spinner ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                                <SyncLoader color="#1e3a5f" size={8} />
                            </div>
                        ) : listTrxData.length === 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "200px", color: "#9ca3af" }}>
                                <FileX size={36} strokeWidth={1} />
                                <p style={{ marginTop: "8px", fontSize: "13px" }}>데이터가 없습니다</p>
                            </div>
                        ) : (
                            <>
                                {listTrxData.map((data, rowIdx) => (
                                    <div
                                        key={rowIdx}
                                        style={{
                                            display: "flex",
                                            minHeight: "40px",
                                            alignItems: "center",
                                            borderBottom: "1px solid #f0f0f0",
                                            backgroundColor: rowIdx % 2 === 0 ? "#fff" : "#fafbfc",
                                            fontSize: "13px",
                                            transition: "background-color 0.1s",
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f0f4ff"}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rowIdx % 2 === 0 ? "#fff" : "#fafbfc"}
                                    >
                                        {columns.map((col, colIdx) => (
                                            <div
                                                key={colIdx}
                                                style={{
                                                    ...colFlex(col),
                                                    textAlign: col.bodyAlign,
                                                    padding: "8px 10px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {renderCell(col, data)}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                <div ref={observerRef} style={{ height: "1px" }} />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── 공통 스타일/컴포넌트 ───
const closeBtnStyle = { background: "none", border: "none", cursor: "pointer", padding: "4px", display: "flex" };
const inputStyle = {
    width: "100%", padding: "8px 10px", fontSize: "13px",
    border: "1px solid #e5e7eb", borderRadius: "4px", outline: "none",
    boxSizing: "border-box",
};
const primaryBtnStyle = {
    padding: "8px 16px", fontSize: "13px", fontWeight: "600",
    backgroundColor: "#1e3a5f", color: "#fff",
    border: "none", borderRadius: "4px", cursor: "pointer",
};
const secondaryBtnStyle = {
    padding: "8px 16px", fontSize: "13px", fontWeight: "500",
    backgroundColor: "#f3f4f6", color: "#374151",
    border: "1px solid #e5e7eb", borderRadius: "4px", cursor: "pointer",
};

function ModalOverlay({ children, onClose }) {
    // 부모에 .intro-y (transform)가 있으면 position: fixed가 뷰포트 기준이 아니게 되므로 body로 포털.
    return createPortal(
        <div
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{ backgroundColor: "#fff", borderRadius: "8px", padding: "20px", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}>{children}</div>
        </div>,
        document.body
    );
}

function InfoRow({ label, value }) {
    return (
        <div>
            <span style={{ color: "#6b7280", fontSize: "12px" }}>{label}</span>
            <div style={{ fontWeight: "500", fontSize: "13px" }}>{value}</div>
        </div>
    );
}

// ─── 조정 모달 ───
function AdjustModal({ target, isMerchant, onClose, onSuccess, setMessage }) {
    const targetId = isMerchant ? target.mid : target.agent_id;
    const targetName = isMerchant ? target.merchant_name : target.agent_name;
    const apiPath = isMerchant
        ? `/api/merchant/wallet/${encodeURIComponent(targetId)}/adjust`
        : `/api/agent-wallet/${encodeURIComponent(targetId)}/adjust`;

    // 멱등키: 모달 열릴 때 1회 생성, 재시도에도 동일 키 유지
    const [idemKey] = useState(() => generateUuid());

    const [step, setStep] = useState(1); // 1: 입력, 2: 확인
    const [sign, setSign] = useState("+");
    const [amountStr, setAmountStr] = useState("");
    const [memo, setMemo] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState("");

    const parseAmount = () => {
        const raw = amountStr.replace(/,/g, "").trim();
        if (!raw) return null;
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n) || n === 0) return null;
        return sign === "-" ? -n : n;
    };

    const formatNumberInput = (v) => {
        const digits = String(v).replace(/[^0-9]/g, "");
        if (!digits) return "";
        return Number(digits).toLocaleString();
    };

    const handleNext = () => {
        const amt = parseAmount();
        if (amt === null) { setFormError("0이 아닌 금액을 입력하세요."); return; }
        if (!memo.trim()) { setFormError("조정 사유를 입력하세요."); return; }
        if (memo.length > 500) { setFormError("사유는 500자를 초과할 수 없습니다."); return; }
        setFormError("");
        setStep(2);
    };

    const handleSubmit = async () => {
        const amt = parseAmount();
        if (amt === null) return;
        setSubmitting(true);
        try {
            const response = await axios.post(
                process.env.REACT_APP_API + apiPath,
                { amount: amt, memo: memo.trim() },
                {
                    withCredentials: true,
                    headers: { "Idempotency-Key": idemKey },
                    validateStatus: () => true,
                }
            );
            const status = response?.status;
            const body = response?.data;
            const code = body?.code;

            if (code === 1000 && body?.data) {
                onSuccess(body.data);
                return;
            }
            if (status === 409) {
                setMessage({ msg: "동일 요청 처리 중입니다. 잠시 후 다시 확인해 주세요." });
                setSubmitting(false);
                return;
            }
            if (status === 400 || code === 4000) {
                setMessage({ msg: body?.msg || "요청이 올바르지 않습니다." });
                setSubmitting(false);
                return;
            }
            if (code === 4001) {
                setFormError(body?.msg || "입력값이 올바르지 않습니다.");
                setStep(1);
                setSubmitting(false);
                return;
            }
            if (code === 4030) {
                setMessage({ msg: body?.msg || "권한이 없습니다." });
                onClose();
                return;
            }
            if (code === 4040) {
                setMessage({ msg: body?.msg || "해당 지갑을 찾을 수 없습니다." });
                onClose();
                return;
            }
            setMessage({ msg: body?.msg || "조정 중 오류가 발생했습니다." });
            setSubmitting(false);
        } catch (err) {
            console.error(err);
            setMessage({ msg: "네트워크 오류. 동일 요청 키로 재시도할 수 있습니다." });
            setSubmitting(false);
        }
    };

    const amt = parseAmount();
    const absAmt = amt !== null ? Math.abs(amt) : 0;
    const isLarge = absAmt >= LARGE_AMOUNT_THRESHOLD;
    const expectedAfter = amt !== null && target.total_balance != null
        ? Number(target.total_balance) + amt
        : null;

    return (
        <ModalOverlay onClose={submitting ? () => { } : onClose}>
            <div style={{ width: "440px", maxWidth: "90vw" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", margin: 0 }}>
                        {step === 1 ? "지갑 잔액 조정" : "조정 확인"}
                    </h3>
                    <button onClick={onClose} disabled={submitting} style={closeBtnStyle}><X size={18} /></button>
                </div>

                {step === 1 ? (
                    <>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "16px", padding: "12px", background: "#f9fafb", borderRadius: "6px" }}>
                            <InfoRow label={isMerchant ? "MID" : "영업자ID"} value={targetId ?? "-"} />
                            <InfoRow label={isMerchant ? "가맹점명" : "영업자명"} value={targetName ?? "-"} />
                            <InfoRow label="총잔액" value={`${changeNum(Number(target.total_balance) || 0)}원`} />
                            <InfoRow label="가용잔액" value={<span style={{ color: "#059669", fontWeight: "600" }}>{changeNum(Number(target.available_balance) || 0)}원</span>} />
                            <InfoRow label="동결잔액" value={<span style={{ color: "#dc2626" }}>{changeNum(Number(target.frozen_balance) || 0)}원</span>} />
                        </div>

                        <div style={{ marginBottom: "12px" }}>
                            <label style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>조정 유형</label>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    type="button"
                                    onClick={() => setSign("+")}
                                    style={{
                                        flex: 1, padding: "8px", fontSize: "13px", fontWeight: "600",
                                        background: sign === "+" ? "#059669" : "#fff",
                                        color: sign === "+" ? "#fff" : "#6b7280",
                                        border: "1px solid", borderColor: sign === "+" ? "#059669" : "#e5e7eb",
                                        borderRadius: "4px", cursor: "pointer",
                                    }}
                                >증액 (+)</button>
                                <button
                                    type="button"
                                    onClick={() => setSign("-")}
                                    style={{
                                        flex: 1, padding: "8px", fontSize: "13px", fontWeight: "600",
                                        background: sign === "-" ? "#dc2626" : "#fff",
                                        color: sign === "-" ? "#fff" : "#6b7280",
                                        border: "1px solid", borderColor: sign === "-" ? "#dc2626" : "#e5e7eb",
                                        borderRadius: "4px", cursor: "pointer",
                                    }}
                                >감액 (−)</button>
                            </div>
                        </div>

                        <div style={{ marginBottom: "12px" }}>
                            <label style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>금액 (원)</label>
                            <input
                                type="text"
                                value={amountStr}
                                onChange={(e) => setAmountStr(formatNumberInput(e.target.value))}
                                placeholder="0"
                                inputMode="numeric"
                                style={inputStyle}
                            />
                        </div>

                        <div style={{ marginBottom: "12px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                                <label style={{ fontSize: "12px", color: "#6b7280" }}>사유 *</label>
                                <span style={{ fontSize: "11px", color: memo.length > 500 ? "#dc2626" : "#9ca3af" }}>{memo.length}/500</span>
                            </div>
                            <textarea
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                                placeholder="조정 사유를 입력하세요"
                                rows={3}
                                maxLength={500}
                                style={{ ...inputStyle, resize: "vertical", minHeight: "60px", fontFamily: "inherit" }}
                            />
                        </div>

                        {formError && (
                            <div style={{ padding: "8px 12px", background: "#fef2f2", color: "#dc2626", fontSize: "12px", borderRadius: "4px", marginBottom: "12px" }}>
                                {formError}
                            </div>
                        )}

                        <div style={{ padding: "10px 12px", background: "#f9fafb", borderRadius: "4px", fontSize: "11px", color: "#6b7280", lineHeight: "1.7", marginBottom: "16px" }}>
                            <div>• frozen / locked 잔액은 조정되지 않습니다 (진행 중 이체 보호).</div>
                            <div>• 감액으로 잔액이 음수가 될 수 있으며, 사후 증액으로 복구 가능합니다.</div>
                            <div>• 실제 은행 이체 없이 원장만 조정됩니다.</div>
                        </div>

                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button onClick={onClose} style={secondaryBtnStyle}>취소</button>
                            <button onClick={handleNext} style={primaryBtnStyle}>다음</button>
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ marginBottom: "16px", padding: "14px", background: "#f9fafb", borderRadius: "6px" }}>
                            <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.8" }}>
                                <div>
                                    <b>{targetName ?? targetId}</b>
                                    <span style={{ color: "#6b7280" }}> ({targetId})</span> 지갑을
                                </div>
                                <div style={{ fontSize: "20px", fontWeight: "700", margin: "8px 0", color: amt > 0 ? "#059669" : "#dc2626" }}>
                                    {amt > 0 ? "+" : "−"}{changeNum(absAmt)}원 {amt > 0 ? "증액" : "감액"}
                                </div>
                                {expectedAfter !== null && (
                                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                        조정 후 총잔액 (예상): <b style={{ color: "#1f2937" }}>{changeNum(expectedAfter)}원</b>
                                        {expectedAfter < 0 && <span style={{ color: "#dc2626", marginLeft: "6px" }}>⚠ 음수</span>}
                                    </div>
                                )}
                                <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #e5e7eb", fontSize: "12px" }}>
                                    <span style={{ color: "#6b7280" }}>사유: </span>
                                    <span style={{ whiteSpace: "pre-wrap" }}>{memo}</span>
                                </div>
                            </div>
                        </div>

                        {isLarge && (
                            <div style={{ padding: "10px 12px", background: "#fef3c7", color: "#92400e", fontSize: "12px", borderRadius: "4px", marginBottom: "12px", fontWeight: "600" }}>
                                ⚠ 1억원 이상의 조정입니다. 금액을 다시 확인하세요.
                            </div>
                        )}

                        <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px" }}>
                            진행하시겠습니까? 이 작업은 즉시 원장에 반영됩니다.
                        </div>

                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button onClick={() => setStep(1)} disabled={submitting} style={secondaryBtnStyle}>수정</button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                style={{ ...primaryBtnStyle, opacity: submitting ? 0.6 : 1, cursor: submitting ? "not-allowed" : "pointer" }}
                            >
                                {submitting ? "처리 중..." : "조정 실행"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </ModalOverlay>
    );
}

// ─── 엑셀 ───
const Excel = (excelData, fileName) => {
    const excelFileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const keys = excelData.length > 0 ? Object.keys(excelData[0]) : [];
    const sheetData = [keys, ...excelData.map((d) => keys.map((k) => d[k]))];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws["!cols"] = Array(keys.length).fill({ wpx: 100 });
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    FileSaver.saveAs(new Blob([buf], { type: excelFileType }), fileName + ".xlsx");
};

export default WalletBalance;
